import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import NumberInput from '@/components/ui/NumberInput';
import { Label } from '@/components/ui/label';
import { AssignedUserSelect } from '@/components/AssignedUserSelect';
import { isAssignableAssignedUser } from '@/components/assigned-user.utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AutocompleteSelect } from '@/components/ui/autocomplete-select';
import { ImageUploadField } from '../retiros/ImageUploadField';
import { CreateResponsableDialog } from './CreateResponsableDialog';
import { CreateOtorganteDialog } from './CreateOtorganteDialog';
import { OTORGANTES_QUERY_KEY, RESPONSABLES_QUERY_KEY, TIPO_TRAMITE_OPTIONS } from '../../constants';
import otrosTramitesService from '../../services/otrosTramitesService';
import type {
  CreateOtorgantePayload,
  CreateOtroTramitePayload,
  DniDocumentsPayload,
  DniDocumentsResponse,
  DniDocumentoRecord,
  DniPersonaTipo,
  GaranteFileRecord,
  GarantePayload,
  OtorganteOption,
  OtroTramiteRecord,
  OtroTramiteType,
  ResponsableOption,
  UserOption,
} from '../../types';

interface ManageOtroTramiteDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  tramite?: OtroTramiteRecord | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: CreateOtroTramitePayload) => Promise<unknown>;
  isSubmitting: boolean;
}

interface DniDraftItem {
  frente: File | null;
  dorso: File | null;
  removeFrente: boolean;
  removeDorso: boolean;
}

interface GaranteDraft {
  /** ID local único (clientId) para React key — NO enviado al backend para garantes nuevos */
  clientId: string;
  /** ID de la fila en DB, presente solo para garantes ya persistidos */
  id?: string;
  isNew: boolean;
  nombre: string;
  frente: File | null;
  dorso: File | null;
  removeFrente: boolean;
  removeDorso: boolean;
  existingFrente: GaranteFileRecord | null;
  existingDorso: GaranteFileRecord | null;
}

type DniDraftState = {
  otorgante: Record<string, DniDraftItem>;
  responsable: Record<string, DniDraftItem>;
};

const createEmptyDniResponse = (): DniDocumentsResponse => ({
  otorgante: {},
  responsable: {},
});

const createEmptyDniDraftItem = (): DniDraftItem => ({
  frente: null,
  dorso: null,
  removeFrente: false,
  removeDorso: false,
});

const formatPersonaOptionLabel = (option: { nombre: string; email?: string }) =>
  option.email ? `${option.nombre} | ${option.email}` : option.nombre;

const syncDraftsWithSelections = (
  current: DniDraftState,
  selectedOtorgantes: OtorganteOption[],
  selectedResponsables: ResponsableOption[]
): DniDraftState => {
  const allowedOtorganteIds = new Set(selectedOtorgantes.map((item) => item.id));
  const allowedResponsableIds = new Set(selectedResponsables.map((item) => item.id));

  const nextOtorgante = Object.fromEntries(
    Object.entries(current.otorgante).filter(([id]) => allowedOtorganteIds.has(id))
  );
  const nextResponsable = Object.fromEntries(
    Object.entries(current.responsable).filter(([id]) => allowedResponsableIds.has(id))
  );

  return {
    otorgante: nextOtorgante,
    responsable: nextResponsable,
  };
};

const buildDniPayload = (
  selectedOtorgantes: OtorganteOption[],
  selectedResponsables: ResponsableOption[],
  dniDrafts: DniDraftState
): DniDocumentsPayload | undefined => {
  const payload: DniDocumentsPayload = {};

  const buildForType = (
    personaTipo: DniPersonaTipo,
    selected: Array<{ id: string }>
  ): Record<string, NonNullable<DniDocumentsPayload[DniPersonaTipo]>[string]> => {
    const data: Record<string, NonNullable<DniDocumentsPayload[DniPersonaTipo]>[string]> = {};

    selected.forEach(({ id }) => {
      const draft = dniDrafts[personaTipo][id];
      if (!draft) {
        return;
      }

      const entry: NonNullable<DniDocumentsPayload[DniPersonaTipo]>[string] = {};
      if (draft.frente) {
        entry.frente = draft.frente;
      }
      if (draft.dorso) {
        entry.dorso = draft.dorso;
      }
      if (draft.removeFrente) {
        entry.removeFrente = true;
      }
      if (draft.removeDorso) {
        entry.removeDorso = true;
      }

      if (Object.keys(entry).length > 0) {
        data[id] = entry;
      }
    });

    return data;
  };

  const otorgantesPayload = buildForType('otorgante', selectedOtorgantes);
  const responsablesPayload = buildForType('responsable', selectedResponsables);

  if (Object.keys(otorgantesPayload).length > 0) {
    payload.otorgante = otorgantesPayload;
  }
  if (Object.keys(responsablesPayload).length > 0) {
    payload.responsable = responsablesPayload;
  }

  return Object.keys(payload).length > 0 ? payload : undefined;
};

export const ManageOtroTramiteDialog: React.FC<ManageOtroTramiteDialogProps> = ({
  open,
  mode,
  tramite,
  onOpenChange,
  onSubmit,
  isSubmitting,
}) => {
  const queryClient = useQueryClient();
  const [selectedOtorgantes, setSelectedOtorgantes] = useState<OtorganteOption[]>([]);
  const [otorganteSearch, setOtorganteSearch] = useState('');
  const [tipo, setTipo] = useState<OtroTramiteType | ''>('');
  const [detalle, setDetalle] = useState('');
  const [copiasTotales, setCopiasTotales] = useState('1');
  const [montoARS, setMontoARS] = useState('0');
  const [montoUSD, setMontoUSD] = useState('0');
  const [responsableSearch, setResponsableSearch] = useState('');
  const [selectedResponsables, setSelectedResponsables] = useState<ResponsableOption[]>([]);
  const [selectedAssignedUser, setSelectedAssignedUser] = useState<UserOption | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [existingDniDocuments, setExistingDniDocuments] = useState<DniDocumentsResponse>(createEmptyDniResponse());
  const [dniDrafts, setDniDrafts] = useState<DniDraftState>({ otorgante: {}, responsable: {} });
  const [garantes, setGarantes] = useState<GaranteDraft[]>([]);
  const [createResponsableState, setCreateResponsableState] = useState({
    open: false,
    nombre: '',
    dni: '',
    telefono: '',
    email: '',
    error: null as string | null,
    isValidatingEmail: false,
  });
  const [createOtorganteState, setCreateOtorganteState] = useState({
    open: false,
    nombre: '',
    dni: '',
    telefono: '',
    email: '',
    error: null as string | null,
    isValidatingEmail: false,
  });

  const selectedOtorganteIds = useMemo(
    () => selectedOtorgantes.map((item) => item.id).sort(),
    [selectedOtorgantes]
  );
  const selectedResponsableIds = useMemo(
    () => selectedResponsables.map((item) => item.id).sort(),
    [selectedResponsables]
  );

  const tramiteDetailQuery = useQuery({
    queryKey: ['otros-tramites', 'detail', tramite?.id],
    queryFn: async () => {
      if (!tramite?.id) return null;
      return otrosTramitesService.getById(tramite.id);
    },
    enabled: open && mode === 'edit' && Boolean(tramite?.id),
    staleTime: 0,
  });

  const otorgantesQuery = useQuery({
    queryKey: [...OTORGANTES_QUERY_KEY, otorganteSearch],
    queryFn: () => otrosTramitesService.searchOtorgantes({ search: otorganteSearch, limit: 12 }),
    staleTime: 1000 * 30,
  });

  const responsablesQuery = useQuery({
    queryKey: [...RESPONSABLES_QUERY_KEY, responsableSearch],
    queryFn: () => otrosTramitesService.searchResponsables({ search: responsableSearch, limit: 12 }),
    staleTime: 1000 * 30,
  });

  const reusableDniQuery = useQuery({
    queryKey: ['otros-tramites', 'dni-documents', selectedOtorganteIds, selectedResponsableIds],
    queryFn: () => otrosTramitesService.getDniDocumentsByPersonas({
      otorganteIds: selectedOtorganteIds,
      responsableIds: selectedResponsableIds,
    }),
    enabled: open && (selectedOtorganteIds.length > 0 || selectedResponsableIds.length > 0),
    staleTime: 1000 * 30,
  });

  const createResponsableMutation = useMutation({
    mutationFn: (payload: { nombre: string; email: string; dni?: string; telefono?: string }) =>
      otrosTramitesService.createResponsable(payload),
    onSuccess: (nuevo) => {
      setSelectedResponsables((prev) => {
        if (prev.some((responsable) => responsable.id === nuevo.id)) {
          return prev;
        }
        return [...prev, nuevo];
      });
      queryClient.invalidateQueries({ queryKey: RESPONSABLES_QUERY_KEY });
      setResponsableSearch('');
    },
  });

  const createOtorganteMutation = useMutation({
    mutationFn: (payload: CreateOtorgantePayload) => otrosTramitesService.createOtorgante(payload),
    onSuccess: (nuevo) => {
      setSelectedOtorgantes((prev) => {
        if (prev.some((otorgante) => otorgante.id === nuevo.id)) {
          return prev;
        }
        return [...prev, nuevo];
      });
      queryClient.invalidateQueries({ queryKey: OTORGANTES_QUERY_KEY });
      setOtorganteSearch('');
      setCreateOtorganteState({ open: false, nombre: '', dni: '', telefono: '', email: '', error: null, isValidatingEmail: false });
    },
  });

  useEffect(() => {
    if (!open) {
      setSelectedOtorgantes([]);
      setOtorganteSearch('');
      setTipo('');
      setDetalle('');
      setCopiasTotales('1');
      setMontoARS('0');
      setMontoUSD('0');
      setResponsableSearch('');
      setSelectedResponsables([]);
      setSelectedAssignedUser(null);
      setExistingDniDocuments(createEmptyDniResponse());
      setDniDrafts({ otorgante: {}, responsable: {} });
      setGarantes([]);
      setFormError(null);
      return;
    }

    const sourceTramite = mode === 'edit' ? (tramiteDetailQuery.data ?? tramite) : null;
    if (mode === 'edit' && sourceTramite) {
      setSelectedOtorgantes(sourceTramite.otorgantes ?? []);
      setTipo(sourceTramite.tipo);
      setDetalle(sourceTramite.detalle ?? '');
      setCopiasTotales(String(sourceTramite.copiasTotales));
      setMontoARS(String(sourceTramite.montoARS));
      setMontoUSD(String(sourceTramite.montoUSD));
      setSelectedResponsables(sourceTramite.responsables ?? []);
      setSelectedAssignedUser(sourceTramite.assignedUsers?.[0] ?? null);
      setExistingDniDocuments(sourceTramite.dniDocuments ?? createEmptyDniResponse());
      setDniDrafts({ otorgante: {}, responsable: {} });
      // Hidratar garantes existentes
      setGarantes(
        (sourceTramite.garantes ?? []).map((g) => ({
          clientId: g.id,
          id: g.id,
          isNew: false,
          nombre: g.nombre,
          frente: null,
          dorso: null,
          removeFrente: false,
          removeDorso: false,
          existingFrente: g.dniFrente,
          existingDorso: g.dniDorso,
        }))
      );
    } else if (mode === 'create') {
      setSelectedOtorgantes([]);
      setTipo('');
      setDetalle('');
      setCopiasTotales('1');
      setMontoARS('0');
      setMontoUSD('0');
      setSelectedResponsables([]);
      setSelectedAssignedUser(null);
      setExistingDniDocuments(createEmptyDniResponse());
      setDniDrafts({ otorgante: {}, responsable: {} });
      setGarantes([]);
    }

    setResponsableSearch('');
    setFormError(null);
  }, [open, mode, tramite, tramiteDetailQuery.data]);

  useEffect(() => {
    setDniDrafts((prev) => syncDraftsWithSelections(prev, selectedOtorgantes, selectedResponsables));
  }, [selectedOtorgantes, selectedResponsables]);

  useEffect(() => {
    if (!reusableDniQuery.data) {
      return;
    }

    setExistingDniDocuments((prev) => {
      const next: DniDocumentsResponse = {
        otorgante: { ...prev.otorgante },
        responsable: { ...prev.responsable },
      };

      const mergePersonaType = (personaTipo: DniPersonaTipo) => {
        const incoming = reusableDniQuery.data?.[personaTipo] ?? {};
        Object.entries(incoming).forEach(([personaId, incomingDocs]) => {
          const currentDocs = next[personaTipo][personaId] ?? {};
          next[personaTipo][personaId] = {
            frente: currentDocs.frente ?? incomingDocs.frente,
            dorso: currentDocs.dorso ?? incomingDocs.dorso,
          };
        });
      };

      mergePersonaType('otorgante');
      mergePersonaType('responsable');

      return next;
    });
  }, [reusableDniQuery.data]);

  const otorganteOptions = useMemo(() => {
    const unique = new Map<string, OtorganteOption>();
    selectedOtorgantes.forEach((otorgante) => {
      unique.set(otorgante.id, otorgante);
    });
    (otorgantesQuery.data ?? []).forEach((otorgante) => {
      if (!unique.has(otorgante.id)) {
        unique.set(otorgante.id, otorgante);
      }
    });
    return Array.from(unique.values());
  }, [otorgantesQuery.data, selectedOtorgantes]);

  const responsableOptions = useMemo(() => {
    const unique = new Map<string, ResponsableOption>();
    selectedResponsables.forEach((responsable) => {
      unique.set(responsable.id, responsable);
    });
    (responsablesQuery.data ?? []).forEach((responsable) => {
      if (!unique.has(responsable.id)) {
        unique.set(responsable.id, responsable);
      }
    });
    return Array.from(unique.values());
  }, [responsablesQuery.data, selectedResponsables]);

  const normalizedOtorganteSearch = otorganteSearch.trim().toLowerCase();
  const normalizedResponsableSearch = responsableSearch.trim().toLowerCase();

  const canSuggestCreateOtorgante = normalizedOtorganteSearch.length > 0
    && !selectedOtorgantes.some((otorgante) => otorgante.nombre.toLowerCase() === normalizedOtorganteSearch)
    && !otorganteOptions.some((otorgante) => otorgante.nombre.toLowerCase() === normalizedOtorganteSearch);

  const canSuggestCreateResponsable = normalizedResponsableSearch.length > 0
    && !selectedResponsables.some((responsable) => responsable.nombre.toLowerCase() === normalizedResponsableSearch)
    && !responsableOptions.some((responsable) => responsable.nombre.toLowerCase() === normalizedResponsableSearch);

  const openCreateResponsableDialog = (nombre: string) => {
    setCreateResponsableState({ open: true, nombre, dni: '', telefono: '', email: '', error: null, isValidatingEmail: false });
  };

  const closeCreateResponsableDialog = () => {
    setCreateResponsableState({ open: false, nombre: '', dni: '', telefono: '', email: '', error: null, isValidatingEmail: false });
    setResponsableSearch('');
  };

  const getCurrentExistingDoc = (
    personaTipo: DniPersonaTipo,
    personaId: string,
    documentoTipo: 'frente' | 'dorso'
  ): DniDocumentoRecord | null => {
    const draft = dniDrafts[personaTipo][personaId];
    if (documentoTipo === 'frente' && (draft?.frente || draft?.removeFrente)) {
      return null;
    }
    if (documentoTipo === 'dorso' && (draft?.dorso || draft?.removeDorso)) {
      return null;
    }
    return existingDniDocuments[personaTipo]?.[personaId]?.[documentoTipo] ?? null;
  };

  const updateDraft = (
    personaTipo: DniPersonaTipo,
    personaId: string,
    updater: (current: DniDraftItem) => DniDraftItem
  ) => {
    setDniDrafts((prev) => {
      const current = prev[personaTipo][personaId] ?? createEmptyDniDraftItem();
      return {
        ...prev,
        [personaTipo]: {
          ...prev[personaTipo],
          [personaId]: updater(current),
        },
      };
    });
  };

  const addGarante = useCallback(() => {
    setGarantes((prev) => [
      ...prev,
      {
        clientId: crypto.randomUUID(),
        isNew: true,
        nombre: '',
        frente: null,
        dorso: null,
        removeFrente: false,
        removeDorso: false,
        existingFrente: null,
        existingDorso: null,
      },
    ]);
  }, []);

  const updateGarante = useCallback((clientId: string, patch: Partial<GaranteDraft>) => {
    setGarantes((prev) =>
      prev.map((g) => (g.clientId === clientId ? { ...g, ...patch } : g))
    );
  }, []);

  const removeGarante = useCallback((clientId: string) => {
    setGarantes((prev) => prev.filter((g) => g.clientId !== clientId));
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (mode === 'edit' && !tramite) {
      setFormError('No se encontró el trámite seleccionado.');
      return;
    }

    if (selectedOtorgantes.length === 0) {
      setFormError('Seleccioná al menos un otorgante.');
      return;
    }

    if (!tipo) {
      setFormError('Seleccioná el tipo de trámite.');
      return;
    }

    if (selectedResponsables.length === 0) {
      setFormError('Seleccioná al menos un responsable.');
      return;
    }

    const totalCopias = Number.parseInt(copiasTotales, 10);
    if (Number.isNaN(totalCopias) || totalCopias <= 0) {
      setFormError('La cantidad de copias debe ser un número positivo.');
      return;
    }

    const montoArsValue = Number.parseFloat(montoARS) || 0;
    const montoUsdValue = Number.parseFloat(montoUSD) || 0;
    const normalizedDetalle = detalle.trim();
    const detallePayload = mode === 'edit'
      ? normalizedDetalle
      : (normalizedDetalle ? normalizedDetalle : undefined);
    const dniDocuments = buildDniPayload(selectedOtorgantes, selectedResponsables, dniDrafts);

    // Construir payload de garantes (filtrar los que no tienen nombre)
    const garantesPayload: GarantePayload[] = garantes
      .filter((g) => g.nombre.trim().length > 0)
      .map((g) => ({
        id: g.isNew ? undefined : g.id,
        nombre: g.nombre.trim(),
        dniFrente: g.frente ?? undefined,
        dniDorso: g.dorso ?? undefined,
        removeFrente: g.removeFrente || undefined,
        removeDorso: g.removeDorso || undefined,
      }));

    setFormError(null);

    try {
      await onSubmit({
        otorganteIds: selectedOtorgantes.map((otorgante) => otorgante.id),
        responsableIds: selectedResponsables.map((responsable) => responsable.id),
        assignedUserIds: selectedAssignedUser ? [selectedAssignedUser.id] : [],
        tipo,
        detalle: detallePayload,
        copiasTotales: totalCopias,
        copiasActuales: mode === 'edit' && tramite ? tramite.copiasActuales : 0,
        montoARS: Math.max(0, montoArsValue),
        montoUSD: Math.max(0, montoUsdValue),
        cobroARS: mode === 'edit' && tramite ? tramite.cobroARS : 'Sin cobrar',
        cobroUSD: mode === 'edit' && tramite ? tramite.cobroUSD : 'Sin cobrar',
        dniDocuments,
        garantes: garantesPayload,
      });
      onOpenChange(false);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 413) {
        setFormError('Los archivos adjuntos son demasiado grandes. Reducí el tamaño de las imágenes e intentá nuevamente.');
      } else {
        setFormError(error instanceof Error ? error.message : 'No pudimos guardar el trámite. Intentá nuevamente.');
      }
    }
  };

  const isLoadingDetail = mode === 'edit' && tramiteDetailQuery.isFetching && !tramiteDetailQuery.data;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-2xl max-h-[90dvh] overflow-y-auto"
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest('[data-slot="dialog-content"]') &&
                (createResponsableState.open || createOtorganteState.open)) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>{mode === 'create' ? 'Nuevo trámite' : 'Editar trámite'}</DialogTitle>
            <DialogDescription>
              {mode === 'create'
                ? 'Cargá los datos principales del trámite. Podés editar los detalles más adelante.'
                : 'Actualizá la información del trámite seleccionado. Guardá los cambios para mantener la trazabilidad.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            {isLoadingDetail && (
              <p className="text-sm text-muted-foreground">Cargando datos completos del trámite...</p>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="otorgantes">Otorgantes</Label>
                <AutocompleteSelect<OtorganteOption>
                  mode="multiple"
                  values={selectedOtorgantes}
                  onChange={(next) => setSelectedOtorgantes(next)}
                  options={otorganteOptions}
                  getOptionLabel={formatPersonaOptionLabel}
                  placeholder="Seleccioná otorgantes"
                  inputValue={otorganteSearch}
                  onInputValueChange={setOtorganteSearch}
                  hideSelectedOptions
                  isLoading={otorgantesQuery.isFetching}
                  emptyMessage="No encontramos otorgantes con ese nombre."
                  createOptionLabel={canSuggestCreateOtorgante ? `Crear "${otorganteSearch.trim()}"` : undefined}
                  onCreate={canSuggestCreateOtorgante ? (value) => {
                    setCreateOtorganteState({
                      open: true,
                      nombre: value,
                      dni: '',
                      telefono: '',
                      email: '',
                      error: null,
                      isValidatingEmail: false,
                    });
                  } : undefined}
                  isCreating={createOtorganteMutation.isPending}
                  disabled={otorgantesQuery.isLoading && !otorgantesQuery.data}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tipo-tramite">Tipo de trámite</Label>
                <Select
                  value={tipo}
                  onValueChange={(value) => setTipo(value as OtroTramiteType)}
                >
                  <SelectTrigger id="tipo-tramite">
                    <SelectValue placeholder="Seleccioná una opción" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPO_TRAMITE_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="detalle-tramite">Detalle del trámite</Label>
                <Textarea
                  id="detalle-tramite"
                  value={detalle}
                  onChange={(event) => setDetalle(event.target.value)}
                  maxLength={1000}
                  placeholder="Agregá contexto adicional para este trámite"
                  className="min-h-[96px]"
                />
                <p className="text-xs text-muted-foreground text-right">{detalle.length}/1000</p>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Responsables</Label>
                <div className="text-xs text-muted-foreground mb-1">
                  Buscá responsables o usuarios del sistema
                </div>
                <AutocompleteSelect<ResponsableOption>
                  mode="multiple"
                  values={selectedResponsables}
                  onChange={(next) => setSelectedResponsables(next)}
                  options={responsableOptions}
                  getOptionLabel={formatPersonaOptionLabel}
                  placeholder="Seleccioná responsables"
                  inputValue={responsableSearch}
                  onInputValueChange={setResponsableSearch}
                  hideSelectedOptions
                  isLoading={responsablesQuery.isFetching}
                  emptyMessage="No encontramos responsables con ese nombre."
                  createOptionLabel={canSuggestCreateResponsable ? `Crear "${responsableSearch.trim()}"` : undefined}
                  onCreate={canSuggestCreateResponsable ? (value) => openCreateResponsableDialog(value) : undefined}
                  isCreating={createResponsableMutation.isPending}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Usuario asignado</Label>
                <AssignedUserSelect
                  value={selectedAssignedUser}
                  onChange={setSelectedAssignedUser}
                  placeholder="Buscar usuario responsable"
                  disabled={isSubmitting || isLoadingDetail}
                  filterOption={isAssignableAssignedUser}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="copias">Cantidad de copias</Label>
                <Input
                  id="copias"
                  type="number"
                  min={1}
                  step={1}
                  value={copiasTotales}
                  onChange={(event) => setCopiasTotales(event.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="monto-ars">Monto (ARS)</Label>
                <NumberInput
                  id="monto-ars"
                  currency={true}
                  decimals={2}
                  min={0}
                  value={montoARS}
                  onChange={(value) => setMontoARS(value)}
                  placeholder="Ingrese el monto en pesos"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="monto-usd">Monto (USD)</Label>
                <NumberInput
                  id="monto-usd"
                  currency={true}
                  decimals={2}
                  min={0}
                  value={montoUSD}
                  onChange={(value) => setMontoUSD(value)}
                  placeholder="Ingrese el monto en dólares"
                />
              </div>
            </div>

            <div className="space-y-4 rounded-lg border border-gray-200 p-4">
              <div>
                <Label>DNI por usuario asignado</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Carga opcional de frente y dorso. Podés subir ahora o completarlo después desde edición.
                </p>
              </div>

              {[...selectedOtorgantes.map((item) => ({ ...item, personaTipo: 'otorgante' as const, etiqueta: 'Otorgante' })),
                ...selectedResponsables.map((item) => ({ ...item, personaTipo: 'responsable' as const, etiqueta: 'Responsable' }))]
                .map((persona) => {
                  const frenteExisting = getCurrentExistingDoc(persona.personaTipo, persona.id, 'frente');
                  const dorsoExisting = getCurrentExistingDoc(persona.personaTipo, persona.id, 'dorso');
                  const draft = dniDrafts[persona.personaTipo][persona.id] ?? createEmptyDniDraftItem();

                  return (
                    <div key={`${persona.personaTipo}-${persona.id}`} className="rounded-md border border-gray-100 p-3 space-y-3">
                      <div>
                        <p className="text-sm font-medium">{persona.nombre}</p>
                        <p className="text-xs text-muted-foreground">{persona.etiqueta}</p>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <ImageUploadField
                          label="DNI Frente"
                          value={draft.frente}
                          existingFile={frenteExisting}
                          onChange={(file) => updateDraft(persona.personaTipo, persona.id, (current) => ({
                            ...current,
                            frente: file,
                            removeFrente: false,
                          }))}
                          onRemoveExisting={() => updateDraft(persona.personaTipo, persona.id, (current) => ({
                            ...current,
                            removeFrente: true,
                          }))}
                          disabled={isSubmitting || isLoadingDetail}
                          required={false}
                        />

                        <ImageUploadField
                          label="DNI Dorso"
                          value={draft.dorso}
                          existingFile={dorsoExisting}
                          onChange={(file) => updateDraft(persona.personaTipo, persona.id, (current) => ({
                            ...current,
                            dorso: file,
                            removeDorso: false,
                          }))}
                          onRemoveExisting={() => updateDraft(persona.personaTipo, persona.id, (current) => ({
                            ...current,
                            removeDorso: true,
                          }))}
                          disabled={isSubmitting || isLoadingDetail}
                          required={false}
                        />
                      </div>
                    </div>
                  );
                })}

              {selectedOtorgantes.length + selectedResponsables.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Seleccioná otorgantes y responsables para habilitar la carga de DNI.
                </p>
              )}
            </div>

            {/* Sección de Garantes */}
            <div className="space-y-4 rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Garantes</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Opcional. Podés agregar garantes con DNI frente y dorso.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addGarante}
                  disabled={isSubmitting || isLoadingDetail}
                >
                  + Agregar garante
                </Button>
              </div>

              {garantes.map((g) => (
                <div key={g.clientId} className="rounded-md border border-gray-100 p-3 space-y-3">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 space-y-1">
                      <Label htmlFor={`garante-nombre-${g.clientId}`} className="text-xs">
                        Nombre del garante
                      </Label>
                      <Input
                        id={`garante-nombre-${g.clientId}`}
                        value={g.nombre}
                        onChange={(e) => updateGarante(g.clientId, { nombre: e.target.value })}
                        placeholder="Nombre completo"
                        disabled={isSubmitting || isLoadingDetail}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="mt-5 shrink-0"
                      onClick={() => removeGarante(g.clientId)}
                      disabled={isSubmitting}
                      title="Quitar garante"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <ImageUploadField
                      label="DNI Frente"
                      value={g.frente}
                      existingFile={g.frente || g.removeFrente ? null : g.existingFrente}
                      onChange={(file) => updateGarante(g.clientId, { frente: file, removeFrente: false })}
                      onRemoveExisting={() => updateGarante(g.clientId, { removeFrente: true })}
                      disabled={isSubmitting || isLoadingDetail}
                      required={false}
                    />
                    <ImageUploadField
                      label="DNI Dorso"
                      value={g.dorso}
                      existingFile={g.dorso || g.removeDorso ? null : g.existingDorso}
                      onChange={(file) => updateGarante(g.clientId, { dorso: file, removeDorso: false })}
                      onRemoveExisting={() => updateGarante(g.clientId, { removeDorso: true })}
                      disabled={isSubmitting || isLoadingDetail}
                      required={false}
                    />
                  </div>
                </div>
              ))}

              {garantes.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No hay garantes. Usá "+ Agregar garante" para sumar uno.
                </p>
              )}
            </div>

            {formError && <p className="text-sm text-rose-600">{formError}</p>}

            <DialogFooter className="gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting || isLoadingDetail}>
                Guardar trámite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <CreateResponsableDialog
        open={createResponsableState.open}
        nombre={createResponsableState.nombre}
        dni={createResponsableState.dni}
        telefono={createResponsableState.telefono}
        email={createResponsableState.email}
        error={createResponsableState.error}
        isSubmitting={createResponsableMutation.isPending || createResponsableState.isValidatingEmail}
        onClose={closeCreateResponsableDialog}
        onChange={(updates) => setCreateResponsableState((prev) => ({ ...prev, ...updates }))}
        onSubmit={async (form) => {
          try {
            setCreateResponsableState((prev) => ({ ...prev, error: null, isValidatingEmail: true }));
            const emailExists = await otrosTramitesService.checkEmailExists(form.email);
            if (emailExists) {
              setCreateResponsableState((prev) => ({
                ...prev,
                error: 'Ya existe un usuario con este email. Buscalo en la lista de responsables.',
                isValidatingEmail: false,
              }));
              return;
            }

            setCreateResponsableState((prev) => ({ ...prev, isValidatingEmail: false }));
            await createResponsableMutation.mutateAsync(form);
            closeCreateResponsableDialog();
          } catch (err) {
            setCreateResponsableState((prev) => ({
              ...prev,
              error: err instanceof Error ? err.message : 'No pudimos crear el responsable. Intentá nuevamente.',
              isValidatingEmail: false,
            }));
          }
        }}
      />

      <CreateOtorganteDialog
        open={createOtorganteState.open}
        nombre={createOtorganteState.nombre}
        dni={createOtorganteState.dni}
        telefono={createOtorganteState.telefono}
        email={createOtorganteState.email}
        error={createOtorganteState.error}
        isSubmitting={createOtorganteMutation.isPending || createOtorganteState.isValidatingEmail}
        onClose={() => setCreateOtorganteState({ open: false, nombre: '', dni: '', telefono: '', email: '', error: null, isValidatingEmail: false })}
        onChange={(updates) => setCreateOtorganteState((prev) => ({ ...prev, ...updates }))}
        onSubmit={async (form) => {
          try {
            setCreateOtorganteState((prev) => ({ ...prev, error: null, isValidatingEmail: true }));
            const emailExists = await otrosTramitesService.checkEmailExists(form.email);
            if (emailExists) {
              setCreateOtorganteState((prev) => ({
                ...prev,
                error: 'Ya existe un usuario con este email. Buscalo en la lista de otorgantes.',
                isValidatingEmail: false,
              }));
              return;
            }

            setCreateOtorganteState((prev) => ({ ...prev, isValidatingEmail: false }));
            await createOtorganteMutation.mutateAsync(form);
          } catch (err) {
            setCreateOtorganteState((prev) => ({
              ...prev,
              error: err instanceof Error ? err.message : 'No pudimos crear el otorgante. Intentá nuevamente.',
              isValidatingEmail: false,
            }));
          }
        }}
      />
    </>
  );
};
